import { Injectable } from '@angular/core';
import { PpHttpParams, PpHttpService } from '@penpencil/common';
import { BehaviorSubject, catchError, map, Observable, of, retry } from 'rxjs';

import { handleError } from '../error-handler/error-handler.service';
import { QuestionObject, QuestionUsage, TagDetails } from './question.model';

@Injectable({
    providedIn: 'root',
})
export class QuestionsService {
    constructor(private apiService: PpHttpService) {}

    languageTab$: BehaviorSubject<any> = new BehaviorSubject([]);
    activeTab: any = {
        isChecked: true,
        name: 'English',
        value: 'eng',
    };

    discardQuestions: Array<any> = [];

    getQuestionsList(query: any) {
        const options = {
            apiPath: 'qbg/questions',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<QuestionObject>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getQuestion(query: any) {
        const options = {
            apiPath: 'qbg/questions/search/text',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<QuestionObject>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getQuestions(query: any, requestId?: string) {
        const options = {
            apiPath: `qbg/questions/request/${requestId}`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<QuestionObject>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getQuestionsById(query: any) {
        const options = {
            apiPath: `qbg/questions/${query.id}`,
        };
        return this.apiService.get<QuestionObject>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getAllQuestions(query: any, body: any) {
        const options = {
            apiPath: `qbg/questions/get-bulk-questions`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.post<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getQuestionVersions(query: any) {
        const options = {
            apiPath: `qbg/question-versions`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<any>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getRequestedQuestions(requestId: string) {
        const options = {
            apiPath: `qbg/requests/${requestId}`,
        };
        return this.apiService.get<any>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    checkDuplicateQuestions(body: any) {
        const options = {
            apiPath: 'qbg/questions/check-duplicate-question',
        };
        return this.apiService.post<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    createQuestions(body: any) {
        const options = {
            apiPath: 'qbg/questions',
        };
        return this.apiService.post<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    createChildQuestions(body: any, query: any) {
        const options = {
            apiPath: `qbg/questions/${query.questionId}/children`,
        };
        return this.apiService.post<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    updateChildQuestions(body: any, query: any) {
        const options = {
            apiPath: `qbg/questions/${query.questionId}/children`,
        };
        return this.apiService.put<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getChildQuestions(query: any) {
        const options = {
            apiPath: `qbg/questions/${query.id}/children`,
        };
        return this.apiService.get<QuestionObject>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    updateQuestions(body: any, query: any) {
        const options = {
            apiPath: `qbg/questions/${query.id}`,
        };
        return this.apiService.put<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    updateVideoEventData(body: any, questionId: string) {
        const options = {
            apiPath: `qbg/questions/${questionId}/video-details-capture`,
        };
        return this.apiService.put<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    bulkVerify(body: any) {
        const options = {
            apiPath: `qbg/questions/verification/bulk-verify`,
        };
        return this.apiService.put<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    rejectQuestion(body: any) {
        const options = {
            apiPath: `qbg/questions/verification/reject`,
        };
        return this.apiService.put<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    bulkUnVerify(body: any) {
        const options = {
            apiPath: `qbg/questions/verification/bulk-unverify`,
        };
        return this.apiService.put<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    wordUpload(body: any, mode?: any) {
        const options = {
            apiPath: `qbg/questions/${mode}-upload`,
        };

        return this.apiService.post<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    jsonUpload(body: any) {
        const options = {
            apiPath: `qbg/questions/json-upload`,
        };
        return this.apiService.post<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    searchByFilter(query?: any, data?: any) {
        const options = {
            apiPath: `qbg/questions/filter`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.post<QuestionObject>(data, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    deleteQuestion(query: any) {
        const options = {
            apiPath: `qbg/questions/${query.id}`,
        };
        return this.apiService.delete<QuestionObject>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    deleteBulkQuestions(ids: Array<string>) {
        const options = {
            apiPath: `qbg/questions/bulk-question-delete`,
        };
        return this.apiService
            .post<QuestionObject>({ uniqueIds: ids }, options)
            .pipe(retry(1), catchError(handleError));
    }

    searchByText(query: any, body: any) {
        const options = {
            apiPath: `qbg/questions/search/text`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.post<QuestionObject>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    searchByImage(query: any) {
        const options = {
            apiPath: 'qbg/questions/search/image',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<any>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    fetchQuestions(query: any) {
        const options = {
            apiPath: `qbg/subjects/${query.subjectId}/sources/${query.sourceId}/question/${query.questionNumber}`,
            params: new PpHttpParams().appendAll(query.filename),
        };
        return this.apiService.get<any>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    fetchAllQuestions() {
        const options = {
            apiPath: 'qbg/subjects',
        };
        return this.apiService.get<any>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getSubjectDetails(query: any) {
        const options = {
            apiPath: 'qbg/subjects/details',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<TagDetails>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getChapterDetails(query: any) {
        const options = {
            apiPath: 'qbg/chapters/details',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<TagDetails>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getTopicDetails(query: any) {
        const options = {
            apiPath: 'qbg/topics/details',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<TagDetails>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getSubTopicDetails(query: any) {
        const options = {
            apiPath: 'qbg/subtopics/details',
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<TagDetails>(options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getContentSolutionImage(body: any) {
        const options = {
            apiPath: 'qbg/questions/get-content-solution-image',
        };
        return this.apiService.post<any>(body, options).pipe(
            retry(1),
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getSearchSuggestion(query: any, body: any) {
        const options = {
            apiPath: `qbg/questions/search/text`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.post<any>(body, options).pipe(
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    changeDisplayOrder(basket_id: string, data: any) {
        const options = {
            apiPath: `qbg/basket/${basket_id}/change-display-order`,
        };
        return this.apiService.post<any>(data, options).pipe(
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getRuleQuestion(query: any, rule_id: any, basket_id: string) {
        const options = {
            apiPath: `qbg/basket/${basket_id}/rule/${rule_id}/rule-questions`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<any>(options).pipe(
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    updateRuleQuestions(body: any, rule_id: any, basket_id: string) {
        const options = {
            apiPath: `qbg/basket/${basket_id}/rule/${rule_id}/select-rule-questions`,
        };
        return this.apiService.post<QuestionObject>(body, options).pipe(
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getBasketSelectedQuestion(query: any, basket_id: string) {
        const options = {
            apiPath: `qbg/basket/${basket_id}/view-basket`,
            params: new PpHttpParams().appendAll(query),
        };
        return this.apiService.get<any>(options).pipe(
            catchError(handleError),
            map((res: any) => res['data'])
        );
    }

    getQuestionUsageBatch(
        questionIds: string[]
    ): Observable<{ statusCode: number; data: QuestionUsage[] }> {
        const options = {
            apiPath: 'qbg/questions/qbg-batch-dpp-details',
        };
        const body = {
            uniqueIds: questionIds,
        };
        return this.apiService.post<any>(body, options).pipe(
            catchError(handleError),
            map((res: any) => res)
        );
    }
}
